// frontend/src/components/mcbim/IfcClassesMenuContent.tsx
import React, { useMemo, useState } from "react";
import {
  Checkbox,
  Box,
  Text,
  Group,
  TextInput,
  SegmentedControl,
  Divider,
  UnstyledButton,
} from "@mantine/core";
import { FaSearch } from "react-icons/fa";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { setIfcClassesToDisplayInRedux } from "../../designSlice";
import {
  getInstanceClassMeta,
  IFC_INSTANCE_CLASS_META,
} from "@multiconsult-group/multiplacer-core";
import {
  selectAllIfcClassesToToggle,
  selectInstanceCountsByClass,
} from "../../../McBim/mcbimItemSelectors";

type TabKey = "ARK" | "RIV" | "RIE" | "OTHER";

const TAB_LABELS: Record<TabKey, string> = {
  ARK: "ARK",
  RIV: "RIV",
  RIE: "RIE",
  OTHER: "Øvrig",
};

const normalize = (s: string) => s.trim().toLowerCase();

// Norsk (Bokmål): Kun innholdet i menyen (ingen Mantine Menu.* avhengigheter).
export const IfcClassesMenuContent: React.FC = () => {
  const dispatch = useAppDispatch();

  const all = useAppSelector(selectAllIfcClassesToToggle);
  const countsByClass = useAppSelector(selectInstanceCountsByClass);
  const ifcClassesToDisplay = useAppSelector((s) => s.design.ifcClassesToDisplay ?? {});

  const [tab, setTab] = useState<TabKey>("ARK");
  const [search, setSearch] = useState("");

  const isActive = (cls: string) => !!ifcClassesToDisplay[cls];

  const classesForTab = useMemo(() => {
    const q = normalize(search);

    return all
      .filter((cls) => {
        const meta = IFC_INSTANCE_CLASS_META[cls];
        const discipline = meta?.discipline; // "ARK" | "RIV" | "RIE" | undefined

        const isInTab = tab === "OTHER" ? discipline == null : discipline === tab;

        if (!isInTab) return false;

        if (!q) return true;

        const display = getInstanceClassMeta(cls);
        return normalize(cls).includes(q) || normalize(display.label ?? "").includes(q);
      })
      .sort((a, b) => a.localeCompare(b));
  }, [all, tab, search]);

  const tabAllSelected = classesForTab.length > 0 && classesForTab.every((c) => isActive(c));
  const tabAnySelected = classesForTab.some((c) => isActive(c));

  const toggleAllInTab = () => {
    if (classesForTab.length === 0) return;

    if (tabAllSelected) {
      classesForTab.forEach((cls) => {
        if (isActive(cls)) dispatch(setIfcClassesToDisplayInRedux(cls));
      });
    } else {
      classesForTab.forEach((cls) => {
        if (!isActive(cls)) dispatch(setIfcClassesToDisplayInRedux(cls));
      });
    }
  };

  const renderRow = (cls: string) => {
    const meta = getInstanceClassMeta(cls);
    const color = meta.color ?? "#000000";
    const label = meta.label ?? cls;
    const count = countsByClass[cls] ?? 0;

    return (
      <Box key={cls} py={4} onMouseDown={(e) => e.preventDefault()}>
        <Checkbox
          checked={isActive(cls)}
          onChange={() => dispatch(setIfcClassesToDisplayInRedux(cls))}
          label={
            <Group gap={8} wrap="nowrap">
              <Box
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  background: color,
                  border: "1px solid rgba(0,0,0,0.15)",
                  flex: "0 0 auto",
                }}
              />
              <Group gap={6} wrap="nowrap">
                <Text size="sm">{label}</Text>
                <Text size="xs" c="dimmed">
                  ({cls})
                </Text>
                <Text size="xs" c="dimmed">
                  · {count.toLocaleString("nb-NO")}
                </Text>
              </Group>
            </Group>
          }
        />
      </Box>
    );
  };

  return (
    <>
      <Box px="sm" py={6}>
        <Text size="xs" fw={600} c="dimmed">
          Vis / skjul IFC-klasser
        </Text>
      </Box>

      <Box px="xs" pb="xs">
        <SegmentedControl
          size="xs"
          fullWidth
          value={tab}
          onChange={(v) => setTab(v as TabKey)}
          data={[
            { value: "ARK", label: TAB_LABELS.ARK },
            { value: "RIV", label: TAB_LABELS.RIV },
            { value: "RIE", label: TAB_LABELS.RIE },
            { value: "OTHER", label: TAB_LABELS.OTHER },
          ]}
        />
      </Box>

      <Box px="xs" pb="xs">
        <TextInput
          size="xs"
          placeholder="Søk (klasse eller navn)…"
          leftSection={<FaSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </Box>

      <Box px="xs" pb="xs">
        <UnstyledButton
          onMouseDown={(e) => e.preventDefault()}
          onClick={toggleAllInTab}
          style={{ width: "100%" }}
        >
          <Text size="sm">
            {tabAllSelected ? "Skjul alle" : tabAnySelected ? "Vis alle" : "Vis alle"}
          </Text>
        </UnstyledButton>
      </Box>

      <Divider />

      <Box px="xs" pb="xs">
        {classesForTab.map(renderRow)}

        {classesForTab.length === 0 && (
          <Box px="xs" py="sm">
            <Text size="xs" c="dimmed">
              Ingen IFC-klasser funnet for {TAB_LABELS[tab]}.
            </Text>
          </Box>
        )}
      </Box>
    </>
  );
};
